import { Fragment, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "@clerk/react";
import { Button, Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ndd/ui";
import { authenticatedApi, ApiRequestError } from "@/lib/api";
import { SOCIAL_NOTES_FLAG, useFeatureEnabled } from "@/lib/use-features";
import { formatLocalDate, weeksSince } from "../../../../shared/local-date";
import { supportTrackLabel } from "../../../../shared/form-registry";
import {
  FORM_EMPLOYMENT_LOADS,
  FORM_MEETING_CADENCES,
  FORM_REVIEW_STATUSES,
  FORM_SUPPORT_TRACKS,
  type FormProfilePutPayload,
  type FormEmploymentLoad,
  type FormMeetingCadence,
  type FormReviewStatus,
  type FormSupportTrack,
} from "../../../../shared/form-types";
import { useFormDirtyGuard } from "./use-form-dirty-guard";
import { DiscardChangesDialog } from "./discard-changes-dialog";

/**
 * Client-safe projection of the `rd_form_profiles` row (server
 * `RdFormProfileRow`). Mirrors the Drizzle select model field-for-field so the
 * client bundle never imports the schema; `GET /api/rds/:id/forms/profile`
 * returns exactly this shape (or null), and FormsSurface passes it through.
 */
export interface FormProfileData {
  id: string;
  rdId: string;
  supportTrack: FormSupportTrack;
  startDate: string | null;
  meetingCadence: FormMeetingCadence | null;
  employmentLoad: FormEmploymentLoad | null;
  ninetyDayReviewStatus: FormReviewStatus | null;
  ninetyDayReviewDate: string | null;
  annualEvaluationStatus: FormReviewStatus | null;
  annualEvaluationDate: string | null;
  contractMinimumHours: number | null;
  currentTargetHours: number | null;
  profileNotes: string | null;
  socialNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

const CARD =
  "overflow-hidden rounded-[14px] border border-sand-200 bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]";
const CARD_HEAD =
  "flex min-h-[48px] items-center justify-between gap-3 border-b border-sand-200 bg-sand-100 px-4 py-2";
const CARD_TITLE =
  "font-sans text-[12px] font-extrabold uppercase tracking-[0.06em] text-foreground";
const FIELD_BASE =
  "h-9 rounded-[10px] bg-sand-50 border-sand-200 text-foreground text-[13px] md:text-[13px] px-3 placeholder:text-sand-400 shadow-none";
const TEXTAREA_BASE =
  "min-h-[60px] resize-y rounded-[10px] border-sand-200 bg-sand-50 px-3 py-2 text-[13px] text-sand-600 shadow-none";
const LABEL_BASE =
  "text-[11px] font-semibold uppercase tracking-wider text-sand-500";

const NONE = "__none__";

const CADENCE_LABELS: Record<FormMeetingCadence, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  as_needed: "As needed",
};
const CADENCE_STRIP_LABELS: Record<FormMeetingCadence, string> = {
  weekly: "weekly",
  biweekly: "biweekly",
  monthly: "monthly",
  as_needed: "as needed",
};
const EMPLOYMENT_LABELS: Record<FormEmploymentLoad, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
};
const REVIEW_STATUS_LABELS: Record<FormReviewStatus, string> = {
  not_scheduled: "Not scheduled",
  scheduled: "Scheduled",
  completed: "Completed",
};

interface FormsProfileCardProps {
  rdId: string;
  profile: FormProfileData | null;
  editing: boolean;
  /** Whether edit affordances are offered (only in the surface's list view). */
  canEdit: boolean;
  onEditStart: () => void;
  onCancel: () => void;
  onSaved: (profile: FormProfileData) => void;
}

export function FormsProfileCard({
  rdId,
  profile,
  editing,
  canEdit,
  onEditStart,
  onCancel,
  onSaved,
}: FormsProfileCardProps) {
  const socialNotesEnabled = useFeatureEnabled(SOCIAL_NOTES_FLAG);
  return (
    <section className={CARD} aria-label="Functional form profile">
      <div className={CARD_HEAD}>
        <h3 className={CARD_TITLE}>Functional form profile</h3>
        {canEdit && !editing && profile && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEditStart}
            className="h-7 rounded-lg border-sand-200 px-[10px] text-[12px]"
          >
            Edit profile
          </Button>
        )}
      </div>

      {editing ? (
        <ProfileEditForm
          rdId={rdId}
          profile={profile}
          socialNotesEnabled={socialNotesEnabled}
          onCancel={onCancel}
          onSaved={onSaved}
        />
      ) : profile ? (
        <ProfileStrip profile={profile} socialNotesEnabled={socialNotesEnabled} />
      ) : (
        <div className="px-4 py-8 text-center text-sand-500">
          <p className="mb-1 font-sans text-[15px] font-semibold text-sand-600">
            No functional form profile yet
          </p>
          <p className="text-sm">
            Set the support track and cadence to start weekly forms.
          </p>
          {canEdit && (
            <div className="mt-4">
              <Button onClick={onEditStart}>Set up profile</Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ProfileStrip({
  profile,
  socialNotesEnabled,
}: {
  profile: FormProfileData;
  socialNotesEnabled: boolean;
}) {
  const segments: ReactNode[] = [
    <strong key="track" className="font-[650] text-foreground">
      {supportTrackLabel(profile.supportTrack)}
    </strong>,
  ];

  if (profile.startDate) {
    segments.push(
      <span key="stage" className="text-sand-400">
        Week {Math.max(1, weeksSince(profile.startDate) + 1)} since start (
        {formatLocalDate(profile.startDate, "short")})
      </span>,
    );
  }
  if (profile.meetingCadence) {
    segments.push(
      <span key="cadence">
        Meets {CADENCE_STRIP_LABELS[profile.meetingCadence]}
      </span>,
    );
  }
  if (
    profile.currentTargetHours !== null &&
    profile.contractMinimumHours !== null
  ) {
    segments.push(
      <span key="hours">
        {profile.currentTargetHours} of {profile.contractMinimumHours} target
        clinical hrs
      </span>,
    );
  }
  if (profile.ninetyDayReviewDate) {
    segments.push(
      <span key="review">
        90-day review due {formatLocalDate(profile.ninetyDayReviewDate, "short")}
      </span>,
    );
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <p className="text-[13.5px] leading-[1.6] text-sand-600">
        {segments.map((segment, index) => (
          <Fragment key={index}>
            {index > 0 && <span className="px-[3px] text-sand-300">·</span>}
            {segment}
          </Fragment>
        ))}
      </p>
      {socialNotesEnabled && profile.socialNotes && (
        <div className="border-t border-sand-200 pt-2">
          <p className={LABEL_BASE}>Social notes</p>
          <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-[1.6] text-sand-600">
            {profile.socialNotes}
          </p>
        </div>
      )}
    </div>
  );
}

function nullableFromSelect<T extends string>(value: string): T | null {
  return value === NONE ? null : (value as T);
}

function ProfileEditForm({
  rdId,
  profile,
  socialNotesEnabled,
  onCancel,
  onSaved,
}: {
  rdId: string;
  profile: FormProfileData | null;
  socialNotesEnabled: boolean;
  onCancel: () => void;
  onSaved: (profile: FormProfileData) => void;
}) {
  const { getToken } = useAuth();
  const [supportTrack, setSupportTrack] = useState<FormSupportTrack>(
    profile?.supportTrack ?? "onboarding_intensive_support",
  );
  const [startDate, setStartDate] = useState(profile?.startDate ?? "");
  const [meetingCadence, setMeetingCadence] = useState<string>(
    profile?.meetingCadence ?? NONE,
  );
  const [employmentLoad, setEmploymentLoad] = useState<string>(
    profile?.employmentLoad ?? NONE,
  );
  const [ninetyDayReviewStatus, setNinetyDayReviewStatus] = useState<string>(
    profile?.ninetyDayReviewStatus ?? NONE,
  );
  const [ninetyDayReviewDate, setNinetyDayReviewDate] = useState(
    profile?.ninetyDayReviewDate ?? "",
  );
  const [annualEvaluationStatus, setAnnualEvaluationStatus] = useState<string>(
    profile?.annualEvaluationStatus ?? NONE,
  );
  const [annualEvaluationDate, setAnnualEvaluationDate] = useState(
    profile?.annualEvaluationDate ?? "",
  );
  const [contractMinimumHours, setContractMinimumHours] = useState(
    profile?.contractMinimumHours?.toString() ?? "",
  );
  const [currentTargetHours, setCurrentTargetHours] = useState(
    profile?.currentTargetHours?.toString() ?? "",
  );
  const [profileNotes, setProfileNotes] = useState(profile?.profileNotes ?? "");
  const [socialNotes, setSocialNotes] = useState(profile?.socialNotes ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savedSnapshot] = useState(() =>
    JSON.stringify({
      supportTrack: profile?.supportTrack ?? "onboarding_intensive_support",
      startDate: profile?.startDate ?? "",
      meetingCadence: profile?.meetingCadence ?? NONE,
      employmentLoad: profile?.employmentLoad ?? NONE,
      ninetyDayReviewStatus: profile?.ninetyDayReviewStatus ?? NONE,
      ninetyDayReviewDate: profile?.ninetyDayReviewDate ?? "",
      annualEvaluationStatus: profile?.annualEvaluationStatus ?? NONE,
      annualEvaluationDate: profile?.annualEvaluationDate ?? "",
      contractMinimumHours: profile?.contractMinimumHours?.toString() ?? "",
      currentTargetHours: profile?.currentTargetHours?.toString() ?? "",
      profileNotes: profile?.profileNotes ?? "",
      socialNotes: profile?.socialNotes ?? "",
    }),
  );
  const currentSnapshot = JSON.stringify({
    supportTrack,
    startDate,
    meetingCadence,
    employmentLoad,
    ninetyDayReviewStatus,
    ninetyDayReviewDate,
    annualEvaluationStatus,
    annualEvaluationDate,
    contractMinimumHours,
    currentTargetHours,
    profileNotes,
    socialNotes,
  });
  const isDirty = currentSnapshot !== savedSnapshot;
  const guard = useFormDirtyGuard(isDirty);

  function parsedHours(value: string): number | null {
    if (value.trim() === "") return null;
    return Number(value);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: FormProfilePutPayload = {
        supportTrack,
        startDate: startDate || null,
        meetingCadence: nullableFromSelect<FormMeetingCadence>(meetingCadence),
        employmentLoad: nullableFromSelect<FormEmploymentLoad>(employmentLoad),
        ninetyDayReviewStatus:
          nullableFromSelect<FormReviewStatus>(ninetyDayReviewStatus),
        ninetyDayReviewDate: ninetyDayReviewDate || null,
        annualEvaluationStatus:
          nullableFromSelect<FormReviewStatus>(annualEvaluationStatus),
        annualEvaluationDate: annualEvaluationDate || null,
        contractMinimumHours: parsedHours(contractMinimumHours),
        currentTargetHours: parsedHours(currentTargetHours),
        profileNotes: profileNotes.trim() || null,
        // Always sent, even when the flag hides the field — omitting it would wipe stored notes on a flag-off save (whole-object PUT).
        socialNotes: socialNotes.trim() || null,
      };
      const response = await authenticatedApi<{ data: FormProfileData }>(getToken, `/api/rds/${rdId}/forms/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onSaved(response.data);
    } catch (submitError) {
      if (submitError instanceof ApiRequestError) {
        const body = submitError.body as { error?: string } | undefined;
        setError(body?.error ?? "Failed to save profile.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-[14px] px-4 py-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Support track" htmlFor="profile-support-track">
          <Select
            value={supportTrack}
            onValueChange={(v) => setSupportTrack(v as FormSupportTrack)}
            disabled={isSubmitting}
          >
            <SelectTrigger id="profile-support-track" className={FIELD_BASE}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORM_SUPPORT_TRACKS.map((track) => (
                <SelectItem key={track} value={track}>
                  {supportTrackLabel(track)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="RD start date" htmlFor="profile-start-date">
          <Input
            id="profile-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Meeting cadence" htmlFor="profile-cadence">
          <NullableSelect
            id="profile-cadence"
            value={meetingCadence}
            onValueChange={setMeetingCadence}
            disabled={isSubmitting}
            options={FORM_MEETING_CADENCES.map((c) => ({
              value: c,
              label: CADENCE_LABELS[c],
            }))}
          />
        </Field>
        <Field label="Employment / load" htmlFor="profile-employment">
          <NullableSelect
            id="profile-employment"
            value={employmentLoad}
            onValueChange={setEmploymentLoad}
            disabled={isSubmitting}
            options={FORM_EMPLOYMENT_LOADS.map((v) => ({
              value: v,
              label: EMPLOYMENT_LABELS[v],
            }))}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="90-day review status" htmlFor="profile-90-status">
          <NullableSelect
            id="profile-90-status"
            value={ninetyDayReviewStatus}
            onValueChange={setNinetyDayReviewStatus}
            disabled={isSubmitting}
            options={FORM_REVIEW_STATUSES.map((v) => ({
              value: v,
              label: REVIEW_STATUS_LABELS[v],
            }))}
          />
        </Field>
        <Field label="90-day review date" htmlFor="profile-90-date">
          <Input
            id="profile-90-date"
            type="date"
            value={ninetyDayReviewDate}
            onChange={(e) => setNinetyDayReviewDate(e.target.value)}
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Annual evaluation status" htmlFor="profile-annual-status">
          <NullableSelect
            id="profile-annual-status"
            value={annualEvaluationStatus}
            onValueChange={setAnnualEvaluationStatus}
            disabled={isSubmitting}
            options={FORM_REVIEW_STATUSES.map((v) => ({
              value: v,
              label: REVIEW_STATUS_LABELS[v],
            }))}
          />
        </Field>
        <Field label="Annual evaluation date" htmlFor="profile-annual-date">
          <Input
            id="profile-annual-date"
            type="date"
            value={annualEvaluationDate}
            onChange={(e) => setAnnualEvaluationDate(e.target.value)}
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Contract minimum hrs" htmlFor="profile-contract-hours">
          <Input
            id="profile-contract-hours"
            type="number"
            min={0}
            step={1}
            value={contractMinimumHours}
            onChange={(e) => setContractMinimumHours(e.target.value)}
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </Field>
        <Field label="Current target hrs" htmlFor="profile-target-hours">
          <Input
            id="profile-target-hours"
            type="number"
            min={0}
            step={1}
            value={currentTargetHours}
            onChange={(e) => setCurrentTargetHours(e.target.value)}
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </Field>
      </div>

      <Field label="Profile notes" htmlFor="profile-notes">
        <Textarea
          id="profile-notes"
          value={profileNotes}
          onChange={(e) => setProfileNotes(e.target.value)}
          disabled={isSubmitting}
          className={TEXTAREA_BASE}
        />
      </Field>

      {/* The PUT payload still carries socialNotes state when the field is
          hidden — the whole-object replace must not wipe stored notes just
          because the flag is off. */}
      {socialNotesEnabled && (
        <Field label="Social notes" htmlFor="profile-social-notes">
          <Textarea
            id="profile-social-notes"
            value={socialNotes}
            onChange={(e) => setSocialNotes(e.target.value)}
            disabled={isSubmitting}
            className={TEXTAREA_BASE}
          />
        </Field>
      )}

      {error && <p className="text-sm text-error">{error}</p>}

      <div className="flex items-center justify-end gap-[10px]">
        <Button
          type="button"
          variant="outline"
          onClick={() => guard.requestClose(onCancel)}
          disabled={isSubmitting}
          className="h-9 rounded-[10px] border-sand-200 px-4 text-[13px] font-medium"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="h-9 rounded-[10px] bg-interactive px-[18px] text-[13px] font-medium text-white shadow-sm hover:bg-interactive-hover"
        >
          {isSubmitting ? "Saving…" : "Save profile"}
        </Button>
      </div>

      <DiscardChangesDialog
        open={guard.showDiscardDialog}
        onOpenChange={guard.handleDiscardDialogOpenChange}
        onDiscard={(e) => {
          e.preventDefault();
          guard.confirmDiscard(onCancel);
        }}
        disabled={isSubmitting}
        description="This profile has edits that haven't been saved. If you leave now, those changes will be lost."
      />
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={htmlFor} className={LABEL_BASE}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function NullableSelect({
  id,
  value,
  onValueChange,
  disabled,
  options,
}: {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled: boolean;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger id={id} className={FIELD_BASE}>
        <SelectValue placeholder="Not set" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Not set</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
